import { createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import {
  BASE_URL,
  getToken,
  getCurrentLanguage,
} from "../../shared/utils.js/utils";
import i18n from "../../i18n/i18n";

// Get Equipment Expenses
export const getEquipmentExpenses = createAsyncThunk(
  "equipmentExpenses/getEquipmentExpenses",
  async (params = {}, { rejectWithValue }) => {
    try {
      const token = getToken();
      const currentLang = getCurrentLanguage();
      if (!token) throw new Error("No authentication token found");
      const queryParams = new URLSearchParams();
      if (params.page) queryParams.append("page", params.page);
      if (params.per_page) queryParams.append("per_page", params.per_page);
      if (params.search) queryParams.append("search", params.search);
      if (params.start_date)
        queryParams.append("start_date", params.start_date);
      if (params.end_date) queryParams.append("end_date", params.end_date);
      if (params.project_id)
        queryParams.append("project_id", params.project_id);

      // If equipment_id is provided, use /equipment/{equipmentId}/expenses
      // Otherwise, use /equipment/expenses
      let url;
      if (params.equipment_id) {
        url = `${BASE_URL}/equipment/${params.equipment_id}/expenses${
          queryParams.toString() ? `?${queryParams.toString()}` : ""
        }`;
      } else {
        url = `${BASE_URL}/equipment/expenses${
          queryParams.toString() ? `?${queryParams.toString()}` : ""
        }`;
      }

      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Accept-Language": currentLang,
        },
      });
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to get equipment expenses!";
      return rejectWithValue(message);
    }
  },
);

// Get Equipment Expenses for specific equipment
export const getEquipmentExpensesByEquipment = createAsyncThunk(
  "equipmentExpenses/getEquipmentExpensesByEquipment",
  async ({ equipmentId, params = {} }, { rejectWithValue }) => {
    try {
      const token = getToken();
      const currentLang = getCurrentLanguage();
      if (!token) throw new Error("No authentication token found");
      const queryParams = new URLSearchParams();
      if (params.page) queryParams.append("page", params.page);
      if (params.per_page) queryParams.append("per_page", params.per_page);
      if (params.search) queryParams.append("search", params.search);
      if (params.start_date)
        queryParams.append("start_date", params.start_date);
      if (params.end_date) queryParams.append("end_date", params.end_date);
      if (params.project_id)
        queryParams.append("project_id", params.project_id);

      const url = `${BASE_URL}/equipment/${equipmentId}/expenses${
        queryParams.toString() ? `?${queryParams.toString()}` : ""
      }`;
      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Accept-Language": currentLang,
        },
      });
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to get equipment expenses!";
      return rejectWithValue(message);
    }
  },
);

// Get Equipment Expense Details
export const getEquipmentExpenseDetails = createAsyncThunk(
  "equipmentExpenses/getEquipmentExpenseDetails",
  async ({ equipmentId, expenseId }, { rejectWithValue }) => {
    try {
      const token = getToken();
      if (!token) throw new Error("No authentication token found");
      const response = await axios.get(
        `${BASE_URL}/equipment/${equipmentId}/expenses/${expenseId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Accept-Language": i18n.language,
          },
        },
      );
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message ||
        "Failed to get equipment expense details!";
      return rejectWithValue(message);
    }
  },
);

// Add Equipment Expense
export const addEquipmentExpense = createAsyncThunk(
  "equipmentExpenses/addEquipmentExpense",
  async ({ equipmentId, expenseData }, { rejectWithValue }) => {
    try {
      const token = getToken();
      const currentLang = getCurrentLanguage();

      if (!token) {
        throw new Error("No authentication token found");
      }

      const response = await axios.post(
        `${BASE_URL}/equipment/${equipmentId}/expenses`,
        expenseData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Accept-Language": currentLang,
          },
        },
      );
      return response.data;
    } catch (error) {
      if (error.response?.data?.errors) {
        const errorMessages = Object.values(error.response.data.errors).flat();
        return rejectWithValue(errorMessages.join(", "));
      }
      const message =
        error.response?.data?.message || "Failed to add equipment expense!";
      return rejectWithValue(message);
    }
  },
);

// Update Equipment Expense
export const updateEquipmentExpense = createAsyncThunk(
  "equipmentExpenses/updateEquipmentExpense",
  async ({ equipmentId, expenseId, expenseData }, { rejectWithValue }) => {
    try {
      const token = getToken();
      const currentLang = getCurrentLanguage();

      if (!token) {
        throw new Error("No authentication token found");
      }

      const response = await axios.put(
        `${BASE_URL}/equipment/${equipmentId}/expenses/${expenseId}`,
        expenseData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Accept-Language": currentLang,
          },
        },
      );
      return response.data;
    } catch (error) {
      if (error.response?.data?.errors) {
        const errorMessages = Object.values(error.response.data.errors).flat();
        return rejectWithValue(errorMessages.join(", "));
      }
      const message =
        error.response?.data?.message || "Failed to update equipment expense!";
      return rejectWithValue(message);
    }
  },
);

// Delete Equipment Expense
export const deleteEquipmentExpense = createAsyncThunk(
  "equipmentExpenses/deleteEquipmentExpense",
  async ({ equipmentId, expenseId }, { rejectWithValue }) => {
    try {
      const token = getToken();
      const currentLang = getCurrentLanguage();
      if (!token) throw new Error("No authentication token found");
      const response = await axios.delete(
        `${BASE_URL}/equipment/${equipmentId}/expenses/${expenseId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Accept-Language": currentLang,
          },
        },
      );
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to delete equipment expense!";
      return rejectWithValue(message);
    }
  },
);
